import React, { useEffect, useId, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Input } from 'antd';
import config from '../../config';
import { useAuthUser } from '../../context/AuthContext';
import useSandboxAdmin from '../../hooks/useSandboxAdmin';
import { getOrganizationSettings, getSignInEndpoints, saveM365RegistrationId } from '../../services/SandboxAdminService';
import { CopyCode, isLocalUrl, sandboxMcpUrl } from './ConnectToolModal';
import ToolIcon from './ToolIcon';
import { downloadAgentPackage } from './m365AgentPackage';

/** The Keycloak clients these connections sign in with (flow-helm-chart files/keycloak/mcp-clients). */
export const M365_CLIENT_ID = 'microsoft-365-copilot';
export const COPILOT_STUDIO_CLIENT_ID = 'microsoft-copilot-studio';
export const SIGN_IN_SCOPES = 'openid profile email offline_access';
export const APIM_REDIRECT = 'https://global.consent.azure-apim.net/redirect';

/** What the admin types in the Teams developer portal's OAuth client registration. */
export function teamsRegistrationFields(mcpUrl, endpoints) {
  const auth = endpoints?.authorizationUrl || '<issuer>/protocol/openid-connect/auth';
  const token = endpoints?.tokenUrl || '<issuer>/protocol/openid-connect/token';
  return [
    'Registration name: Mia-Platform Flow',
    `Base URL: ${mcpUrl}`,
    'Restrict usage by org: My organization only',
    'Restrict usage by app: Any Teams app',
    `Client ID: ${M365_CLIENT_ID}`,
    'Client secret: (leave empty)',
    `Authorization endpoint: ${auth}`,
    `Token endpoint: ${token}`,
    `Refresh endpoint: ${token}`,
    `Scope: ${SIGN_IN_SCOPES}`,
    'Enable Proof Key for Code Exchange (PKCE): On',
  ].join('\n');
}

/** What the maker types in Copilot Studio's Model Context Protocol tool (OAuth 2.0 › Manual). */
export function copilotStudioFields(mcpUrl, endpoints) {
  const auth = endpoints?.authorizationUrl || '<issuer>/protocol/openid-connect/auth';
  const token = endpoints?.tokenUrl || '<issuer>/protocol/openid-connect/token';
  return [
    'Server name: mia-platform-flow',
    'Server description: Mia-Platform Flow sandbox: projects, code and previews',
    `Server URL: ${mcpUrl}`,
    'Authentication: OAuth 2.0',
    'Type: Manual',
    `Client ID: ${COPILOT_STUDIO_CLIENT_ID}`,
    'Client secret: (from Keycloak, see below)',
    `Authorization URL: ${auth}`,
    `Token URL template: ${token}`,
    `Refresh URL: ${token}`,
    `Scopes: ${SIGN_IN_SCOPES}`,
  ].join('\n');
}

function Step({ n, children }) {
  return (
    <li className="org-ai-step">
      <span aria-hidden="true" className="org-ai-step-n">{n}</span>
      <div className="org-ai-step-body">{children}</div>
    </li>
  );
}

Step.propTypes = { n: PropTypes.number.isRequired, children: PropTypes.node };

const endpointsShape = PropTypes.shape({ issuer: PropTypes.string, authorizationUrl: PropTypes.string, tokenUrl: PropTypes.string });

/**
 * One organization connection, laid out like a row of Your AI tools (YourAiTools): its mark, name, "Admins only", one
 * line about it and its status when known, and Setup, which opens its step-by-step setup below (closed at first).
 */
function OrgAiCard({ id, name, about, status = null, children }) {
  const [open, setOpen] = useState(false);
  const bodyId = useId();
  return (
    <section aria-labelledby={id} className={`connections-tool org-ai-card${open ? ' is-open' : ''}`}>
      <div className="org-ai-card-row">
        <ToolIcon label={name} size={20} />
        <span className="connections-tool-text">
          <span className="org-ai-card-name">
            <strong id={id}>{name}</strong>
            <span className="sbx-pill">Admins only</span>
          </span>
          <span>{about}</span>
          {status && <span className="org-ai-card-status">{status}</span>}
        </span>
        <Button
          aria-controls={bodyId}
          aria-expanded={open}
          aria-label={`${open ? 'Hide the setup' : 'Setup'} of ${name}`}
          onClick={() => setOpen((was) => !was)}
          size="small"
          type="link"
        >
          {open ? 'Hide setup' : 'Setup'}
        </Button>
      </div>
      <div className="org-ai-card-body" hidden={!open} id={bodyId}>
        {open && children}
      </div>
    </section>
  );
}

OrgAiCard.propTypes = { id: PropTypes.string.isRequired, name: PropTypes.string.isRequired, about: PropTypes.string.isRequired, status: PropTypes.string, children: PropTypes.node };

function Microsoft365Card({ mcpUrl, endpoints, savedId, onSaved }) {
  const [value, setValue] = useState(savedId || '');
  const [state, setState] = useState({ saving: false, message: null, error: null });
  useEffect(() => { setValue(savedId || ''); }, [savedId]);
  const dirty = value.trim() !== (savedId || '');

  async function save() {
    setState({ saving: true, message: null, error: null });
    try {
      const saved = await saveM365RegistrationId(value);
      onSaved(saved?.aiTools?.microsoft365Copilot?.oauthRegistrationId || null);
      setState({ saving: false, message: value.trim() ? 'Saved. Other sandbox admins see it too.' : 'Cleared.', error: null });
    } catch (error) {
      setState({ saving: false, message: null, error: error.message || 'Saving failed.' });
    }
  }

  function download() {
    try {
      downloadAgentPackage({ mcpUrl, registrationId: savedId });
    } catch (error) {
      setState({ saving: false, message: null, error: error.message || 'The package could not be made.' });
    }
  }

  return (
    <OrgAiCard
      about="A Mia-Platform Flow agent for everyone in Microsoft 365 Copilot and Teams, with the sandbox as its tools."
      id="org-ai-m365"
      name="Microsoft 365 Copilot"
      status={savedId ? 'Registration ID saved' : 'Not set up yet'}
    >
      <ol className="org-ai-steps">
        <Step n={1}>
          <span>In the Teams developer portal (dev.teams.microsoft.com) › Tools › OAuth client registration › Register client, fill in:</span>
          <CopyCode code={teamsRegistrationFields(mcpUrl, endpoints)} label="Copy the Teams registration details" />
        </Step>
        <Step n={2}>
          <label htmlFor="org-ai-m365-id">Save, copy the OAuth client registration ID and paste it here:</label>
          <span className="org-ai-row">
            <Input
              aria-label="OAuth client registration ID"
              id="org-ai-m365-id"
              onChange={(event) => setValue(event.target.value)}
              placeholder="OAuth client registration ID"
              value={value}
            />
            <Button disabled={!dirty || state.saving} loading={state.saving} onClick={save}>Save</Button>
          </span>
        </Step>
        <Step n={3}>
          <span className="org-ai-row">
            <Button disabled={!savedId || dirty} onClick={download} type="primary">Download agent package</Button>
          </span>
          <span className="connections-note">
            {savedId ? 'A zip with the agent (manifest.json, declarativeAgent.json, ai-plugin.json and its icons), made with the saved ID.' : 'Save the registration ID first.'}
          </span>
        </Step>
        <Step n={4}>
          <span>
            Upload it in the Microsoft 365 admin center (Agents) for everyone or chosen users; people open Copilot › Agents ›
            Mia-Platform Flow and sign in.
          </span>
        </Step>
      </ol>
      {state.message && <span className="connections-note" role="status">{state.message}</span>}
      {state.error && <Alert message={state.error} showIcon type="error" />}
    </OrgAiCard>
  );
}

Microsoft365Card.propTypes = { mcpUrl: PropTypes.string.isRequired, endpoints: endpointsShape, savedId: PropTypes.string, onSaved: PropTypes.func.isRequired };

function CopilotStudioCard({ mcpUrl, endpoints }) {
  return (
    <OrgAiCard about="The sandbox as a Model Context Protocol tool in the agents your makers build in Copilot Studio." id="org-ai-studio" name="Copilot Studio">
      <ol className="org-ai-steps">
        <Step n={1}>
          <span>In Copilot Studio, open the agent › Tools › Add a tool › New tool › Model Context Protocol, and fill in:</span>
          <CopyCode code={copilotStudioFields(mcpUrl, endpoints)} label="Copy the Copilot Studio tool details" />
        </Step>
        <Step n={2}>
          <span>
            Select Create, then copy the callback URL Copilot Studio shows. Unless it starts with {APIM_REDIRECT}, send it to
            the Flow admin, who adds it to the {COPILOT_STUDIO_CLIENT_ID} client’s redirect URIs in Keycloak.
          </span>
        </Step>
      </ol>
      <span className="connections-note">
        <strong>The client secret</strong> is in Keycloak: admin console › Clients › {COPILOT_STUDIO_CLIENT_ID} › Credentials.
        Flow does not store it (v1): copy it from there into Copilot Studio yourself.
      </span>
    </OrgAiCard>
  );
}

CopilotStudioCard.propTypes = { mcpUrl: PropTypes.string.isRequired, endpoints: endpointsShape };

/**
 * Connections › Organization AI connections: Microsoft 365 Copilot and Copilot Studio, which the organization sets up
 * once, by an admin, in Microsoft's portals. Shown only to sandbox admins (hooks/useSandboxAdmin.js); the Microsoft 365
 * Copilot OAuth client registration ID is kept in the sandbox admin's organization settings, so every admin sees it.
 * Each is a compact card (OrgAiCard) whose setup opens on demand.
 */
export default function OrgAiConnections() {
  const user = useAuthUser();
  const admin = useSandboxAdmin(user?.sub || user?.id || user?.email, { enabled: Boolean(config.sandboxFirst || config.vfsServiceUrl) });
  const [savedId, setSavedId] = useState(null);
  const [endpoints, setEndpoints] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const mcpUrl = sandboxMcpUrl();

  useEffect(() => {
    if (!admin) { return undefined; }
    let alive = true;
    getOrganizationSettings()
      .then((org) => { if (alive) { setSavedId(org?.aiTools?.microsoft365Copilot?.oauthRegistrationId || null); } })
      .catch((error) => { if (alive) { setLoadError(error.message || 'The organization settings could not be read.'); } });
    getSignInEndpoints().then((found) => { if (alive) { setEndpoints(found); } });
    return () => { alive = false; };
  }, [admin]);

  if (!admin) { return null; }
  return (
    <section aria-labelledby="org-ai-head" className="sbx-card connections-tools org-ai">
      <div className="connections-tools-head">
        <div className="connections-tools-text">
          <h2 id="org-ai-head">Organization AI connections</h2>
          <p>
            AI tools your organization connects once, for everyone, in Microsoft’s portals. Only sandbox admins see this
            section; people then find them in their AI tools.
          </p>
        </div>
      </div>
      {isLocalUrl(mcpUrl) && (
        <Alert message={`These tools connect from Microsoft’s cloud, so they can't reach ${mcpUrl}. Use a Flow that is reachable from the internet.`} showIcon type="warning" />
      )}
      {loadError && <Alert message={loadError} showIcon type="error" />}
      <div className="connections-tools-list org-ai-list">
        <Microsoft365Card endpoints={endpoints} mcpUrl={mcpUrl} onSaved={setSavedId} savedId={savedId} />
        <CopilotStudioCard endpoints={endpoints} mcpUrl={mcpUrl} />
      </div>
    </section>
  );
}
